import { Check, ChevronDown, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { TalentOsActiveCandidate } from '../hooks/use-api.js';

interface TalentOsCandidateMultiSelectProps {
  candidates: TalentOsActiveCandidate[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  loading?: boolean;
  error?: boolean;
  ariaLabel: string;
}

function titleCase(value: string | null | undefined): string {
  return String(value || '')
    .replaceAll('_', ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function applicationDate(application: TalentOsActiveCandidate['applications'][number]): string {
  const value =
    application.applied_at ??
    application.ae_applied_at ??
    application.application_created_at ??
    application.job_posted_at;
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString();
}

function candidateField(value: unknown): string {
  if (Array.isArray(value)) return value.map(String).filter(Boolean).join(', ');
  return typeof value === 'string' ? value.trim() : '';
}

function applicationSummary(candidate: TalentOsActiveCandidate): string {
  const applications = candidate.applications ?? [];
  const labels = applications.slice(0, 2).map((application) => {
    const job = [application.job_title, application.job_company].filter(Boolean).join(' — ');
    const date = applicationDate(application);
    return [titleCase(application.application_status), job || 'Job details unavailable', date]
      .filter(Boolean)
      .join(' · ');
  });
  if (applications.length > 2) labels.push(`+${applications.length - 2} more applications`);
  return labels.join(' | ');
}

function candidateTitle(candidate: TalentOsActiveCandidate): string {
  return [
    candidate.name,
    candidate.candidate_number == null ? '' : `Candidate #${candidate.candidate_number}`,
    candidate.email,
    candidate.phone,
    candidate.linkedin_url,
    candidateField(candidate.target_roles) && `Roles: ${candidateField(candidate.target_roles)}`,
    candidateField(candidate.target_industries) &&
      `Industries: ${candidateField(candidate.target_industries)}`,
    candidate.location_preference && `Location: ${candidate.location_preference}`,
    candidate.work_mode_preference && `Work mode: ${candidate.work_mode_preference}`,
    candidate.available_start_date && `Available: ${candidate.available_start_date}`,
    applicationSummary(candidate),
  ]
    .filter(Boolean)
    .join(' | ');
}

export default function TalentOsCandidateMultiSelect({
  candidates,
  selectedIds,
  onChange,
  disabled = false,
  loading = false,
  error = false,
  ariaLabel,
}: TalentOsCandidateMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const selected = candidates.filter((candidate) => selectedIds.includes(candidate.id));

  useEffect(() => {
    if (!open) return;
    const handleOutsideClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [open]);

  const toggleCandidate = (id: string) => {
    onChange(
      selectedIds.includes(id)
        ? selectedIds.filter((selectedId) => selectedId !== id)
        : [...selectedIds, id]
    );
  };

  return (
    <div
      ref={containerRef}
      className="relative min-w-0"
      onClick={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        className="flex min-h-8 w-full max-w-[280px] items-center gap-1 rounded border border-slate-200 bg-white px-2 py-1 text-left text-xs hover:border-indigo-300 disabled:cursor-not-allowed disabled:bg-slate-50"
        disabled={disabled}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((value) => !value)}
      >
        <span className="flex min-w-0 flex-1 flex-wrap gap-1">
          {selected.length > 0 ? (
            selected.map((candidate) => (
              <span
                key={candidate.id}
                className="inline-flex max-w-full items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-indigo-700"
                title={candidateTitle(candidate)}
              >
                <span className="max-w-[180px] truncate">{candidate.name}</span>
                <span
                  role="button"
                  tabIndex={0}
                  aria-label={`Remove ${candidate.name}`}
                  className="cursor-pointer rounded-full hover:bg-indigo-100"
                  onClick={(event) => {
                    event.stopPropagation();
                    toggleCandidate(candidate.id);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      toggleCandidate(candidate.id);
                    }
                  }}
                >
                  <X size={12} />
                </span>
              </span>
            ))
          ) : (
            <span className="text-slate-400">
              {loading
                ? 'Loading candidates…'
                : error
                  ? 'TalentOS feed unavailable'
                  : 'Select candidates'}
            </span>
          )}
        </span>
        <ChevronDown size={14} className="shrink-0 text-slate-400" />
      </button>
      {open && (
        <div
          role="listbox"
          aria-label={`${ariaLabel} options`}
          aria-multiselectable="true"
          className="absolute left-0 top-full z-50 mt-1 max-h-64 w-[280px] overflow-y-auto rounded-lg border border-slate-200 bg-white p-1 shadow-lg"
        >
          {loading && <div className="px-3 py-2 text-xs text-slate-400">Loading candidates…</div>}
          {!loading && error && (
            <div className="px-3 py-2 text-xs text-rose-600">
              TalentOS candidates could not be loaded. Refresh and try again.
            </div>
          )}
          {!loading && !error && candidates.length === 0 && (
            <div className="px-3 py-2 text-xs text-slate-400">No eligible candidates found.</div>
          )}
          {candidates.map((candidate) => {
            const isSelected = selectedIds.includes(candidate.id);
            return (
              <button
                key={candidate.id}
                type="button"
                role="option"
                aria-selected={isSelected}
                className="flex w-full items-center gap-2 rounded px-2 py-2 text-left text-xs text-slate-700 hover:bg-indigo-50"
                onClick={() => toggleCandidate(candidate.id)}
                title={candidateTitle(candidate)}
              >
                <span
                  className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                    isSelected
                      ? 'border-indigo-600 bg-indigo-600 text-white'
                      : 'border-slate-300 bg-white'
                  }`}
                >
                  {isSelected && <Check size={12} />}
                </span>
                <span className="min-w-0 truncate">
                  <span className="block truncate font-medium">{candidate.name}</span>
                  <span className="block truncate text-[10px] text-slate-500">
                    {applicationSummary(candidate) || 'No application details available'}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
